import React, { useState, useEffect } from 'react';
import { 
  Calendar, PoundSterling, Users, Sparkles, TrendingUp, TrendingDown,
  Briefcase, UserPlus, ChevronRight, Loader2, RefreshCw,
  CalendarRange, CalendarClock, Receipt, Minus, Target, Trophy
} from 'lucide-react';
import * as api from '../services/api';
import { subscribeToRefresh } from '../utils/dataSync';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Clickable Stat Tile
const StatTile = ({ 
  title, 
  value, 
  subtitle, 
  icon: Icon, 
  iconBg, 
  iconColor, 
  onClick,
  isClickable = true,
  valuePrefix = '',
  valueSuffix = ''
}) => (
  <button 
    onClick={onClick}
    disabled={!isClickable}
    className={`
      bg-white rounded-2xl p-5 border border-zinc-100 text-left w-full
      ${isClickable ? 'hover:shadow-lg hover:border-emerald-200 hover:scale-[1.02] cursor-pointer' : 'cursor-default'}
      transition-all duration-200 group
    `}
    data-testid={`stat-tile-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-start justify-between">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${iconBg}`}>
        <Icon className={iconColor} size={24} strokeWidth={1.5} />
      </div>
      {isClickable && (
        <ChevronRight 
          size={20} 
          className="text-zinc-300 group-hover:text-emerald-500 group-hover:translate-x-1 transition-all" 
        />
      )}
    </div>
    <div className="mt-4">
      <p className="text-sm font-medium text-zinc-500">{title}</p>
      <p className="text-3xl font-bold text-zinc-900 mt-1">
        {valuePrefix}{typeof value === 'number' ? value.toLocaleString() : value}{valueSuffix}
      </p>
      {subtitle && (
        <p className="text-xs text-zinc-400 mt-1">{subtitle}</p>
      )}
    </div>
  </button>
);

// Large Feature Tile
const FeatureTile = ({ 
  title, 
  value, 
  description,
  icon: Icon, 
  gradient,
  onClick 
}) => (
  <button 
    onClick={onClick}
    className={`
      rounded-2xl p-6 text-left w-full text-white
      hover:shadow-xl hover:scale-[1.02] cursor-pointer
      transition-all duration-200 group ${gradient}
    `}
    data-testid={`feature-tile-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-start justify-between">
      <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
        <Icon size={28} strokeWidth={1.5} />
      </div>
      <ChevronRight 
        size={24} 
        className="opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all" 
      />
    </div>
    <div className="mt-6">
      <p className="text-4xl font-bold">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      <p className="text-lg font-semibold mt-1 opacity-90">{title}</p>
      {description && (
        <p className="text-sm opacity-70 mt-1">{description}</p>
      )}
    </div>
  </button>
);

const Overview = ({ onNavigate, user }) => {
  const [stats, setStats] = useState(null);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  
  // Check if user is a Worker - they see restricted data
  const userRole = user?.role || 'Owner';
  const isWorker = userRole === 'Worker';

  // Fetch user info for company name and logo
  const loadUserInfo = async () => {
    try {
      const token = api.getToken();
      
      // Get user info
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Get settings for company logo
      const settingsRes = await fetch(`${API_URL}/api/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      let userData = {};
      let settingsData = {};
      
      if (res.ok) {
        userData = await res.json();
      }
      
      if (settingsRes.ok) {
        settingsData = await settingsRes.json();
      }
      
      // Merge user data with settings (company_logo from settings takes priority)
      setUserInfo({
        ...userData,
        company_logo: settingsData.company_logo || userData.company_logo,
        company_name: settingsData.company_name || userData.company_name
      });
    } catch (err) {
      console.error('Error loading user info:', err);
    }
  };

  // Fetch live stats
  // `silent=true` skips the loading flash — used for background polls and
  // cross-page sync events. The initial mount passes silent=false so the user
  // sees a spinner on first load.
  const loadStats = async (silent = true) => {
    if (!silent) {
      setIsLoading(true);
    }
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/dashboard/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setStats(data);
        setLastUpdated(new Date());
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUserInfo();
    loadStats(false); // initial load — show spinner
    // Refresh every 30 seconds — silent (no loading flash)
    const interval = setInterval(() => loadStats(true), 30000);
    return () => clearInterval(interval);
  }, []);

  // Subscribe to centralized data sync - auto refresh when any related data changes
  useEffect(() => {
    const unsubscribes = [
      subscribeToRefresh('overview', () => loadStats()),
      subscribeToRefresh('dashboard', () => loadStats()),
    ];
    return () => unsubscribes.forEach(unsub => unsub());
  }, []);

  const handleNavigate = (page) => {
    if (onNavigate) {
      onNavigate(page);
    }
  };

  if (isLoading && !stats) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-zinc-600 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 p-6" data-testid="overview-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          {userInfo?.company_logo && (
            <img 
              src={userInfo.company_logo} 
              alt="Company logo" 
              className="w-12 h-12 rounded-xl object-contain bg-white border border-zinc-200"
            />
          )}
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">
              {userInfo?.company_name || 'Your Business'}
            </h1>
            <p className="text-sm text-zinc-500">
              {lastUpdated && `Last updated: ${lastUpdated.toLocaleTimeString()}`}
            </p>
          </div>
        </div>
        <button 
          onClick={loadStats}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-zinc-200 rounded-xl hover:bg-zinc-50 transition-colors text-sm font-medium text-zinc-600"
        >
          <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Worker View - Simplified Dashboard */}
      {isWorker ? (
        <>
          {/* Worker Stats - Just Jobs Today */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <StatTile
              title="My Jobs Today"
              value={stats?.jobs_today || 0}
              subtitle="Assigned to you"
              icon={Calendar}
              iconBg="bg-purple-100"
              iconColor="text-purple-600"
              onClick={() => handleNavigate('work-planner')}
            />

            <StatTile
              title="My Jobs This Week"
              value={stats?.week_job_count || 0}
              subtitle="Scheduled for this week"
              icon={Briefcase}
              iconBg="bg-emerald-100"
              iconColor="text-emerald-600"
              onClick={() => handleNavigate('work-planner')}
            />
          </div>

          {/* Worker Quick Actions */}
          <div className="bg-white rounded-2xl border border-zinc-100 p-6 mb-6">
            <h2 className="font-semibold text-zinc-900 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleNavigate('work-planner')}
                className="flex items-center gap-3 p-4 bg-zinc-50 hover:bg-zinc-100 rounded-xl transition-colors"
              >
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Calendar size={20} className="text-purple-600" />
                </div>
                <span className="font-medium text-zinc-700 text-sm">Work Planner</span>
              </button>
              
              <button
                onClick={() => handleNavigate('day-pilot')}
                className="flex items-center gap-3 p-4 bg-zinc-50 hover:bg-zinc-100 rounded-xl transition-colors"
              >
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Briefcase size={20} className="text-emerald-600" />
                </div>
                <span className="font-medium text-zinc-700 text-sm">Today</span>
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Main Stats Grid - ADMIN/OWNER VIEW */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            {/* Total Customers (excluding ad-hoc) */}
            <StatTile
              title="Regular Customers"
              value={stats?.total_customers || 0}
              subtitle="Excludes one-off jobs"
              icon={Users}
              iconBg="bg-blue-100"
              iconColor="text-blue-600"
              onClick={() => handleNavigate('customers')}
            />

            {/* Customers Added This Week */}
            <StatTile
              title="Added This Week"
              value={stats?.customers_this_week || 0}
              subtitle="New customers"
              icon={UserPlus}
              iconBg="bg-emerald-100"
              iconColor="text-emerald-600"
              onClick={() => {
                sessionStorage.setItem('customerFilter', 'this-week');
                handleNavigate('customers');
              }}
            />

            {/* Jobs Today */}
            <StatTile
              title="Jobs Today"
              value={stats?.jobs_today || 0}
              subtitle="Scheduled for today"
              icon={Calendar}
              iconBg="bg-purple-100"
              iconColor="text-purple-600"
              onClick={() => handleNavigate('work-planner')}
            />

            {/* New Leads */}
            <StatTile
              title="New Leads"
              value={stats?.new_leads || 0}
              subtitle="Awaiting contact"
              icon={Sparkles}
              iconBg="bg-amber-100"
              iconColor="text-amber-600"
              onClick={() => handleNavigate('leads')}
            />

            {/* Churn Rate */}
            <StatTile
              title="Churn Rate"
              value={`${stats?.churn_rate ?? 0}%`}
              subtitle={`${stats?.churn_lost_30d || 0} lost in 30 days`}
              icon={TrendingDown}
              iconBg="bg-rose-100"
              iconColor="text-rose-600"
              onClick={() => handleNavigate('customers')}
            />
          </div>

          {/* Feature Tiles Row - FINANCIAL DATA */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Customers Owing - Large Tile */}
            <FeatureTile
              title="Customers Owing"
              value={`£${(stats?.total_owing || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`}
              description={`${stats?.customers_owing_count || 0} customers with balance`}
              icon={PoundSterling}
              gradient="bg-gradient-to-br from-rose-500 to-pink-600"
              onClick={() => handleNavigate('owing')}
            />

            {/* This Week Total */}
            <FeatureTile
              title="This Week"
              value={`£${(stats?.week_total || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`}
              description={`${stats?.week_job_count || 0} jobs scheduled`}
              icon={TrendingUp}
              gradient="bg-gradient-to-br from-emerald-500 to-teal-600"
              onClick={() => handleNavigate('work-planner')}
            />
          </div>

          {/* Revenue Insights — recurring job-price based, excludes ad-hoc & inactive customers */}
          <div className="bg-white rounded-2xl border border-zinc-100 p-6 mb-6" data-testid="revenue-insights-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-semibold text-zinc-900 flex items-center gap-2">
                  <TrendingUp size={18} className="text-emerald-600" />
                  Revenue Insights
                </h2>
                <p className="text-xs text-zinc-500 mt-0.5">Banked from completed jobs · projections from recurring prices</p>
              </div>
              <button
                onClick={() => handleNavigate('rounds')}
                className="text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                data-testid="revenue-insights-rounds-link"
              >
                View by round
                <ChevronRight size={14} />
              </button>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              {/* Last month — banked */}
              <div
                className="rounded-xl bg-gradient-to-br from-zinc-50 to-slate-100 border border-zinc-200 p-4"
                data-testid="revenue-last-month-tile"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Calendar size={14} className="text-zinc-600" />
                  <p className="text-xs font-medium text-zinc-600 uppercase tracking-wide">Last month</p>
                </div>
                <p className="text-2xl font-bold text-zinc-900">
                  £{(stats?.revenue_prev_month_actual || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-zinc-500 mt-1">Banked from completed jobs</p>
              </div>

              {/* This month — LIVE */}
              <div
                className="rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 p-4"
                data-testid="revenue-month-live-tile"
              >
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp size={14} className="text-emerald-600" />
                  <p className="text-xs font-medium text-emerald-700 uppercase tracking-wide">This month</p>
                  <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">LIVE</span>
                </div>
                <p className="text-2xl font-bold text-emerald-900">
                  £{(stats?.revenue_month_live || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-emerald-700/70 mt-1">
                  £{(stats?.revenue_month_actual || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })} banked · skips come off
                </p>
              </div>

              {/* Skipped this month */}
              <div
                className="rounded-xl bg-gradient-to-br from-rose-50 to-orange-50 border border-rose-100 p-4"
                data-testid="revenue-skipped-tile"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Minus size={14} className="text-rose-600" />
                  <p className="text-xs font-medium text-rose-700 uppercase tracking-wide">Skipped this month</p>
                </div>
                <p className="text-2xl font-bold text-rose-900">
                  £{(stats?.skipped_month_value || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-rose-700/70 mt-1">{stats?.skipped_month_count || 0} job{(stats?.skipped_month_count || 0) === 1 ? '' : 's'} skipped</p>
              </div>

              <div
                className="rounded-xl bg-gradient-to-br from-purple-50 to-fuchsia-50 border border-purple-100 p-4"
                data-testid="revenue-annual-tile"
              >
                <div className="flex items-center gap-2 mb-1">
                  <CalendarClock size={14} className="text-purple-600" />
                  <p className="text-xs font-medium text-purple-700 uppercase tracking-wide">Per year</p>
                </div>
                <p className="text-2xl font-bold text-purple-900">
                  £{(stats?.revenue_annual || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-purple-700/70 mt-1">Annualised projection</p>
              </div>

              <div
                className="rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100 p-4"
                data-testid="revenue-avg-per-job-tile"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Receipt size={14} className="text-amber-600" />
                  <p className="text-xs font-medium text-amber-700 uppercase tracking-wide">Avg per job</p>
                </div>
                <p className="text-2xl font-bold text-amber-900">
                  £{(stats?.revenue_avg_per_job || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-amber-700/70 mt-1">Recurring visit</p>
              </div>
            </div>
          </div>

          {/* Growth Goals — motivational milestone boxes */}
          {(() => {
            const totalCust = stats?.total_customers || 0;
            const monthly = stats?.revenue_monthly || 0;
            if (!totalCust || !monthly) return null;
            const custMilestone = Math.ceil((totalCust + 1) / 100) * 100;
            const custToGo = custMilestone - totalCust;
            const custPct = Math.min(100, ((totalCust - (custMilestone - 100)) / 100) * 100);
            const perCust = monthly / totalCust;
            const ladder = [10000, 12000, 15000, 17500, 20000];
            // Below £10k the target is £10k; after that, climb £1k at a time
            const nextTarget = monthly < 10000 ? 10000 : Math.floor(monthly / 1000) * 1000 + 1000;
            const prevTarget = monthly < 10000 ? 0 : nextTarget - 1000;
            const custNeeded = Math.max(1, Math.ceil((nextTarget - monthly) / perCust));
            const revPct = Math.min(100, ((monthly - prevTarget) / (nextTarget - prevTarget)) * 100);
            const roadPct = Math.min(100, (monthly / 20000) * 100);
            const fmtK = (v) => (v % 1000 === 0 ? `£${v / 1000}k` : `£${(v / 1000).toFixed(1)}k`);
            return (
              <div className="bg-white rounded-2xl border border-zinc-100 p-6 mb-6" data-testid="growth-goals-card">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="font-semibold text-zinc-900 flex items-center gap-2">
                      <Trophy size={18} className="text-amber-500" />
                      Growth Goals
                    </h2>
                    <p className="text-xs text-zinc-500 mt-0.5">One average customer adds £{perCust.toFixed(2)}/month (avg visit £{(stats?.revenue_avg_per_job || 0).toFixed(2)}, most visits are 6+ weeks apart)</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {/* Next 100 customers */}
                  <div className="rounded-xl bg-gradient-to-br from-emerald-600 to-teal-700 text-white p-5" data-testid="goal-customers-box">
                    <div className="flex items-center gap-2 mb-1">
                      <Users size={14} className="text-emerald-200" />
                      <p className="text-[11px] font-semibold text-emerald-100 uppercase tracking-wider">Get to</p>
                    </div>
                    <p className="text-4xl font-black leading-none">{custMilestone}</p>
                    <p className="text-sm font-semibold text-emerald-100 mt-1">customers</p>
                    <p className="text-2xl font-bold mt-3" data-testid="goal-customers-countdown">{custToGo} <span className="text-sm font-semibold text-emerald-200">to go</span></p>
                    <div className="mt-3 h-2 bg-white/20 rounded-full overflow-hidden">
                      <div className="h-full bg-white rounded-full transition-all" style={{ width: `${custPct}%` }} />
                    </div>
                    <p className="text-[11px] text-emerald-200 mt-1.5">You're on {totalCust}</p>
                  </div>

                  {/* Next revenue milestone */}
                  <div className="rounded-xl bg-gradient-to-br from-sky-600 to-indigo-700 text-white p-5" data-testid="goal-revenue-box">
                      <div className="flex items-center gap-2 mb-1">
                        <Target size={14} className="text-sky-200" />
                        <p className="text-[11px] font-semibold text-sky-100 uppercase tracking-wider">Get to</p>
                      </div>
                      <p className="text-4xl font-black leading-none">{fmtK(nextTarget)}</p>
                      <p className="text-sm font-semibold text-sky-100 mt-1">a month</p>
                      <p className="text-2xl font-bold mt-3" data-testid="goal-revenue-countdown">{custNeeded} <span className="text-sm font-semibold text-sky-200">customers to go</span></p>
                      <div className="mt-3 h-2 bg-white/20 rounded-full overflow-hidden">
                        <div className="h-full bg-white rounded-full transition-all" style={{ width: `${revPct}%` }} />
                      </div>
                    <p className="text-[11px] text-sky-200 mt-1.5">£{monthly.toLocaleString('en-GB', { maximumFractionDigits: 0 })}/month now</p>
                  </div>

                  {/* Road to £20k */}
                  <div className="rounded-xl bg-gradient-to-br from-zinc-800 to-zinc-900 text-white p-5" data-testid="goal-road-box">
                    <div className="flex items-center gap-2 mb-1">
                      <Trophy size={14} className="text-amber-400" />
                      <p className="text-[11px] font-semibold text-zinc-300 uppercase tracking-wider">The road to</p>
                    </div>
                    <p className="text-4xl font-black leading-none text-amber-400">£20k</p>
                    <p className="text-sm font-semibold text-zinc-300 mt-1">a month</p>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {ladder.map(t => (
                        <button
                          key={t}
                          onClick={() => setSelectedMilestone(selectedMilestone === t ? null : t)}
                          className={`px-2 py-0.5 rounded-full text-[11px] font-bold transition-colors ${
                            monthly >= t
                              ? 'bg-amber-400 text-zinc-900'
                              : selectedMilestone === t
                                ? 'bg-white text-zinc-900 ring-2 ring-amber-400'
                                : 'bg-white/10 text-zinc-300 hover:bg-white/20'
                          }`}
                          data-testid={`milestone-chip-${t}`}
                        >
                          {monthly >= t ? '✓ ' : ''}{fmtK(t)}
                        </button>
                      ))}
                    </div>
                    {selectedMilestone && (
                      <p className="text-xs font-semibold text-amber-300 mt-2" data-testid="milestone-detail">
                        {monthly >= selectedMilestone
                          ? `✓ ${fmtK(selectedMilestone)} hit — nice work`
                          : `${Math.max(1, Math.ceil((selectedMilestone - monthly) / perCust))} more customers to hit ${fmtK(selectedMilestone)}`}
                      </p>
                    )}
                    <div className="mt-3 h-2 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${roadPct}%` }} />
                    </div>
                    <p className="text-[11px] text-zinc-400 mt-1.5">{Math.round(roadPct)}% of the way there</p>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Quick Actions */}
          <div className="bg-white rounded-2xl border border-zinc-100 p-6 mb-6">
            <h2 className="font-semibold text-zinc-900 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <button
                onClick={() => handleNavigate('work-planner')}
                className="flex items-center gap-3 p-4 bg-zinc-50 hover:bg-zinc-100 rounded-xl transition-colors"
              >
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Calendar size={20} className="text-purple-600" />
                </div>
                <span className="font-medium text-zinc-700 text-sm">Work Planner</span>
              </button>
              
              <button
                onClick={() => handleNavigate('customers')}
                className="flex items-center gap-3 p-4 bg-zinc-50 hover:bg-zinc-100 rounded-xl transition-colors"
              >
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users size={20} className="text-blue-600" />
                </div>
                <span className="font-medium text-zinc-700 text-sm">Customers</span>
              </button>
              
              <button
                onClick={() => handleNavigate('invoices')}
                className="flex items-center gap-3 p-4 bg-zinc-50 hover:bg-zinc-100 rounded-xl transition-colors"
              >
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Briefcase size={20} className="text-amber-600" />
                </div>
                <span className="font-medium text-zinc-700 text-sm">Invoices</span>
              </button>
              
              <button
                onClick={() => handleNavigate('leads')}
                className="flex items-center gap-3 p-4 bg-zinc-50 hover:bg-zinc-100 rounded-xl transition-colors"
              >
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Sparkles size={20} className="text-emerald-600" />
                </div>
                <span className="font-medium text-zinc-700 text-sm">Leads</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Overview;
